import { useEffect, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight, ChevronDown, Minus, TriangleAlert } from 'lucide-react';
import {
  formatMd,
  serializeMd,
  tickEstimatedSales,
  type CashInBoxEmployeeRow,
  type CashInBoxesSummary,
  type DashboardPayload,
  type DeviceCategoryUsage,
  type EstimatedSales,
  type FloorDeviceTile,
  type LowStockItem,
  type MdString,
  type MonthlySalesPoint,
  type OccupancyDeviceStatus,
  type OccupancySnapshot,
  type PeriodComparison,
  type PeriodSummary,
  type TargetProgress,
  type TopCustomer,
  type TopDeviceRow,
} from '@gc/shared';

import { templateArtFor } from '@/features/devices/DeviceArtwork';

/**
 * The tiles of the Dashboard (SPEC §5.1), split out of the page so the page
 * itself stays a readable description of the layout.
 *
 * Two rules run through every component here:
 *
 * - **Money is `formatMd` over the transported decimal string.** No component
 *   parses, adds or rounds a monetary value; the arithmetic already happened in
 *   `@gc/shared`, and re-doing it in a tile is how two screens start disagreeing.
 * - **Zero is a legitimate reading, not a missing one.** A quiet morning and a
 *   venue that opened yesterday both produce all-zero payloads, so tiles render
 *   their zeroes plainly. Only genuinely empty *lists* get a caption, and that
 *   caption says what is true ("no trade yet"), never that something failed.
 */

export type MoneyFormatter = (millidinars: MdString) => string;

/** Money bound to the venue's own symbol and the reader's locale. */
export function useMoney(currencySymbol: string): MoneyFormatter {
  const { i18n } = useTranslation();
  return (millidinars: MdString) =>
    formatMd(millidinars, { currencySymbol, locale: i18n.language });
}

/* -------------------------------------------------------------------------- */
/* Shells                                                                     */
/* -------------------------------------------------------------------------- */

export function Panel({
  title,
  caption,
  actions,
  className = '',
  children,
}: {
  title: string;
  caption?: string;
  /** Header-end controls (the chart's Day/Month chips) — card `.chead .tog`. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`rounded-card border border-subtle bg-raised p-4 ${className}`}>
      {/* Panel titles per the approved cards: 12px semibold, normal case —
          the uppercase annotation voice belongs to the KPI overlines only. */}
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-semibold text-content">{title}</h2>
        {caption ? <span className="text-xs text-content-muted">{caption}</span> : null}
        {actions ? <div className="ms-auto flex items-center gap-1.5">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm text-content-muted">{children}</p>;
}

/* -------------------------------------------------------------------------- */
/* Headline figures                                                           */
/* -------------------------------------------------------------------------- */

/**
 * One money line.
 *
 * `emphasised` is what the DAY/MONTH toggle drives: the selected period's tiles
 * carry the accent border and the larger figure, the other period stays legible
 * beside it rather than disappearing — an owner comparing today against the
 * month should not have to toggle back and forth to do it.
 */
export function StatTile({
  label,
  value,
  hint,
  emphasised = false,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: ReactNode;
  emphasised?: boolean;
  tone?: 'neutral' | 'danger' | 'positive';
}) {
  return (
    <div
      className={[
        'rounded-card border bg-raised px-4 py-[13px]',
        emphasised ? 'border-primary/60' : 'border-subtle',
      ].join(' ')}
    >
      {/* Overline caption then the figure — the schematic annotation voice. */}
      <p className="text-[10px] uppercase tracking-[0.22em] text-content-muted">{label}</p>
      {/* One figure size for every tile (card `.kpi b`): emphasis is a matter
          of light, not of type scale. */}
      <p
        className={[
          'tabular mt-[5px] text-[22px] leading-tight font-semibold',
          // The venue's headline money glows; refills read green (money that
          // arrived as deposits); every other figure stays quiet.
          tone === 'danger'
            ? 'text-danger'
            : tone === 'positive'
              ? 'text-available'
              : emphasised
                ? 'text-primary [text-shadow:var(--glow-primary)]'
                : 'text-content',
        ].join(' ')}
      >
        {value}
      </p>
      {hint ? <div className="mt-1 text-xs text-content-secondary">{hint}</div> : null}
    </div>
  );
}

/**
 * Estimated sales — the one figure on this screen that moves on its own.
 *
 * Every other tile is the server's arithmetic rendered verbatim, and this one
 * still is: `tickEstimatedSales` lives in `@gc/shared` beside the function that
 * produced the payload, so the number the browser carries forward is the same
 * number the next poll will land on rather than a second, slightly different
 * idea of what a sitting is worth.
 *
 * ELAPSED IS MEASURED FROM ARRIVAL, not from the payload's `generatedAt`. Staff
 * PCs run with whatever clock Windows last agreed on, and a till three minutes
 * fast would otherwise credit the venue with three minutes of takings the
 * moment the page loaded — visibly, since the figure would jump on every poll.
 *
 * The interval only exists while something is actually accruing: on a closed
 * floor the accrual rate is zero, the tile is still correct, and nothing ticks.
 */
export function EstimatedSalesTile({
  estimate,
  label,
  hint,
  money,
}: {
  estimate: EstimatedSales;
  label: string;
  hint?: ReactNode;
  money: MoneyFormatter;
}) {
  const { totalMd, accrualPerHourMd } = estimate;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    setElapsedSeconds(0);
    if (BigInt(accrualPerHourMd) === 0n) return;

    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1_000,
    );
    return () => window.clearInterval(timer);
    // A new payload with different figures restarts the clock; an identical one
    // deliberately does not, so the count keeps running across a quiet poll.
  }, [totalMd, accrualPerHourMd]);

  return (
    <StatTile
      label={label}
      value={money(serializeMd(tickEstimatedSales(estimate, elapsedSeconds)))}
      emphasised
      hint={hint}
    />
  );
}

const FLOOR_TILE_CLASS: Record<OccupancyDeviceStatus, string> = {
  AVAILABLE: 'border-subtle text-content-secondary',
  BUSY: 'border-busy/60 bg-busy/10 text-content',
  ABOUT_TO_END: 'border-ending/60 bg-ending/10 text-content',
  OUT_OF_SERVICE: 'border-danger/60 bg-danger/10 text-content-muted line-through',
};

const FLOOR_LAMP_CLASS: Record<OccupancyDeviceStatus, string> = {
  AVAILABLE: 'bg-available shadow-[var(--glow-available)]',
  BUSY: 'bg-busy shadow-[var(--glow-busy)]',
  ABOUT_TO_END: 'bg-ending shadow-[var(--glow-ending)]',
  OUT_OF_SERVICE: 'bg-danger shadow-[var(--glow-danger)]',
};

/**
 * A tile's hover label: the same four words the counts row above it uses. It
 * once built `dashboard.floorState.<STATUS>`, a block no locale ever had, so
 * every tooltip read the raw key in all three languages.
 */
const FLOOR_STATE_LABEL: Record<OccupancyDeviceStatus, string> = {
  AVAILABLE: 'dashboard.occupancy.available',
  BUSY: 'dashboard.occupancy.busy',
  ABOUT_TO_END: 'dashboard.occupancy.aboutToEnd',
  OUT_OF_SERVICE: 'dashboard.occupancy.outOfService',
};

/**
 * The floor as a mini map (owner request, replacing the four-count strip):
 * every device is a named tile coloured by its live state, grouped by
 * category, so the owner sees WHICH machines are busy — not just how many.
 *
 * Same `liveDeviceStatus` derivation as the Device List, same snapshot as the
 * occupancy counts, which stay as a summary row: the numbers and the tiles can
 * never disagree because they are the same array read twice.
 */
export function FloorGrid({
  occupancy,
  devices,
}: {
  occupancy: OccupancySnapshot;
  devices: readonly FloorDeviceTile[];
}) {
  const { t } = useTranslation();
  // The named tiles are detail on demand (owner request): the cards say how
  // the floor is doing; WHO exactly is busy unfolds when asked.
  const [tilesOpen, setTilesOpen] = useState(false);

  const groups: Array<{ category: string; tiles: FloorDeviceTile[] }> = [];
  for (const device of devices) {
    const group = groups.find((entry) => entry.category === device.category);
    if (group) group.tiles.push(device);
    else groups.push({ category: device.category, tiles: [device] });
  }

  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <header className="flex flex-wrap items-center gap-4">
        <h2 className="text-xs font-semibold text-content">{t('dashboard.floor.title')}</h2>
        <FloorCounts occupancy={occupancy} />
        {devices.length > 0 ? (
          <button
            type="button"
            onClick={() => setTilesOpen((open) => !open)}
            aria-expanded={tilesOpen}
            className="ms-auto inline-flex items-center gap-1 rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary transition-colors hover:bg-hover"
          >
            {t(tilesOpen ? 'dashboard.floor.hideDevices' : 'dashboard.floor.showDevices')}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${tilesOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
        ) : null}
      </header>

      {devices.length === 0 ? (
        <EmptyNote>{t('dashboard.occupancy.noDevices')}</EmptyNote>
      ) : (
        <div className="space-y-3">
          {/*
            The showcase row, in the venue's OWN brand (the approved mock):
            centred fixed-width cards on the dashboard's raised surface, a soft
            brand-tinted halo behind the machine's artwork riding over the top
            edge, and the cyan→violet band at the foot carrying the name and
            the one number that matters — how many are free right now. Brand
            variables, not literals, so a venue that re-themes itself in
            settings re-themes these cards with it. The named tiles behind the
            toggle remain the ground truth; these cards are the same array
            summed, so the two can never disagree.
          */}
          <div className="flex flex-wrap justify-center gap-4 pt-8">
            {groups.map(({ category, tiles }) => {
              const free = tiles.filter((tile) => tile.status === 'AVAILABLE').length;
              // Photo wins, template otherwise — the Device List's own rule,
              // through its own resolver, so a card can never render bare.
              const photo =
                (tiles.find((tile) => tile.imageUrl)?.imageUrl ?? null) ||
                (tiles[0] ? templateArtFor(tiles[0].deviceType, category) : null);
              return (
                <div
                  key={category}
                  className="relative w-[14.25rem] rounded-[22px] border border-white/[0.07] bg-raised shadow-[0_12px_28px_rgba(0,0,0,0.5)]"
                >
                  {/* The brand halo the artwork sits in front of. */}
                  <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-[5.5rem] rounded-t-[22px]"
                    style={{
                      background:
                        'radial-gradient(ellipse 70% 90% at 50% 0%, color-mix(in srgb, var(--brand-secondary) 28%, transparent), color-mix(in srgb, var(--brand-primary) 10%, transparent) 55%, transparent 75%)',
                    }}
                  />
                  {photo ? (
                    <img
                      src={photo}
                      alt=""
                      loading="lazy"
                      className="relative mx-auto -mt-[1.9rem] block max-h-[6.5rem] max-w-[78%] object-contain drop-shadow-[0_12px_14px_rgba(0,0,0,0.6)]"
                    />
                  ) : (
                    <div aria-hidden className="h-[4.5rem]" />
                  )}
                  {/* The foot band: name left, the free count huge on the right. */}
                  <div
                    className="relative m-[7px] flex items-center justify-between gap-2 rounded-2xl px-4 py-2.5 text-[color:var(--brand-primary-fg)]"
                    style={{
                      background:
                        'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)',
                    }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold">{category}</span>
                      <span className="block text-[10.5px] font-semibold opacity-75">
                        {t('dashboard.floor.freeOf', { free, total: tiles.length })}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-3xl font-extrabold leading-none">
                      {free}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {tilesOpen
            ? groups.map(({ category, tiles }) => (
                <div key={category} className="flex flex-wrap items-center gap-1.5">
                  <span className="w-20 shrink-0 text-[10px] uppercase tracking-[0.18em] text-content-muted">
                    {category}
                  </span>
                  {tiles.map((tile) => (
                    <span
                      key={tile.id}
                      title={t(FLOOR_STATE_LABEL[tile.status])}
                      className={`inline-flex items-center gap-1.5 rounded-control border px-2 py-1 text-[11px] ${FLOOR_TILE_CLASS[tile.status]}`}
                    >
                      <i
                        className={`h-1.5 w-1.5 rounded-full ${FLOOR_LAMP_CLASS[tile.status]}`}
                        aria-hidden
                      />
                      {tile.name}
                    </span>
                  ))}
                </div>
              ))
            : null}
        </div>
      )}
    </section>
  );
}

/** The four state counts, now the grid's summary row. */
function FloorCounts({ occupancy }: { occupancy: OccupancySnapshot }) {
  const { t } = useTranslation();

  const lamps: { count: number; label: string; lamp: string }[] = [
    {
      count: occupancy.available,
      label: t('dashboard.occupancy.available'),
      lamp: 'bg-available shadow-[var(--glow-available)]',
    },
    {
      count: occupancy.busy,
      label: t('dashboard.occupancy.busy'),
      lamp: 'bg-busy shadow-[var(--glow-busy)]',
    },
    {
      count: occupancy.aboutToEnd,
      label: t('dashboard.occupancy.aboutToEnd'),
      lamp: 'bg-ending shadow-[var(--glow-ending)]',
    },
    {
      count: occupancy.outOfService,
      label: t('dashboard.occupancy.outOfService'),
      lamp: 'bg-danger shadow-[var(--glow-danger)]',
    },
  ];

  if (occupancy.total === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-4">
      {lamps.map(({ count, label, lamp }) => (
        <span key={label} className="flex items-center gap-1.5 text-xs text-content-secondary">
          <i className={`h-2 w-2 rounded-full ${lamp}`} aria-hidden />
          <span className="tabular">{count}</span> <span className="lowercase">{label}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Which devices took the most money in the selected period, best first.
 *
 * A list, not a chart (owner request). The figures are the same session
 * valuations the usage table reads, folded per device — the panels can never
 * disagree about one machine. Rank is visual weight only; the money column is
 * the content.
 */
export function TopDevicesPanel({
  rows,
  money,
}: {
  rows: readonly TopDeviceRow[];
  money: MoneyFormatter;
}) {
  const { t } = useTranslation();

  return (
    <Panel title={t('dashboard.topDevices.title')}>
      {rows.length === 0 ? (
        <EmptyNote>{t('dashboard.topDevices.empty')}</EmptyNote>
      ) : (
        <ol className="space-y-1">
          {rows.map((row, index) => (
            <li
              key={row.deviceId}
              className="flex items-center gap-2.5 border-b border-subtle py-1.5 last:border-b-0"
            >
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                  index === 0 ? 'bg-primary/15 text-primary' : 'bg-overlay text-content-secondary'
                }`}
              >
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-content">{row.name}</span>
                <span className="block truncate text-[10.5px] text-content-muted">
                  {row.category}
                  {' · '}
                  {t('dashboard.topDevices.sessionCount', { count: row.sessions })}
                </span>
              </span>
              <span className="tabular shrink-0 text-sm font-semibold text-content">
                {money(row.revenueMd)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

/**
 * The trailing-year sales trend beside the Monthly sales tile.
 *
 * Plain flex bars, no chart library — twelve numbers do not need axes. Heights
 * are scaled against the best month, so the shape of the year reads instantly;
 * the exact figure is the tooltip, because the figure that MATTERS (this
 * month's) is already printed on the tile this panel accompanies.
 */
export function MonthlyTrendPanel({
  series,
  money,
}: {
  series: readonly MonthlySalesPoint[];
  money: MoneyFormatter;
}) {
  const { t, i18n } = useTranslation();
  const peak = Math.max(...series.map((point) => point.salesIqd), 1);

  return (
    <Panel title={t('dashboard.monthlyTrend.title')}>
      <div className="flex h-24 items-end gap-1.5">
        {series.map((point) => {
          const label = new Date(`${point.month}-01T00:00:00.000Z`).toLocaleDateString(
            i18n.language,
            { month: 'short', timeZone: 'UTC' },
          );
          const current = point === series[series.length - 1];
          return (
            <div
              key={point.month}
              className="group flex min-w-0 flex-1 flex-col items-center gap-1"
              title={`${point.month} · ${money(point.salesMd)}`}
            >
              <div className="flex h-16 w-full items-end">
                <div
                  className={`w-full rounded-t ${current ? 'bg-primary shadow-[var(--glow-primary)]' : 'bg-primary/35 group-hover:bg-primary/60'}`}
                  // 2% floor so a zero month still draws a visible baseline
                  // notch rather than a hole in the year.
                  style={{ height: `${Math.max(2, (point.salesIqd / peak) * 100)}%` }}
                />
              </div>
              <span className="truncate text-[9px] uppercase tracking-wide text-content-muted">
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/**
 * "+12.4% vs previous period".
 *
 * `changePercent` is deliberately `null` when the previous period took nothing —
 * growth from zero is undefined, not infinite — so that case gets its own words
 * instead of an arrow pointing at "∞%".
 */
export function ComparisonHint({
  comparison,
  label,
}: {
  comparison: PeriodComparison;
  label: string;
}) {
  const { t } = useTranslation();

  if (comparison.changePercent === null) {
    return <span className="text-content-muted">{t('dashboard.kpi.noBaseline')}</span>;
  }

  const Icon =
    comparison.direction === 'up'
      ? ArrowUpRight
      : comparison.direction === 'down'
        ? ArrowDownRight
        : Minus;

  const tone =
    comparison.direction === 'up'
      ? 'text-available'
      : comparison.direction === 'down'
        ? 'text-danger'
        : 'text-content-muted';

  return (
    <span className={`inline-flex items-center gap-1 ${tone}`}>
      {/* The arrow points up/down, never start/end — it is not a direction of
          travel across the page, so RTL must not flip it. */}
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span className="tabular">
        {comparison.changePercent > 0 ? '+' : ''}
        {comparison.changePercent}%
      </span>
      <span className="text-content-muted">{label}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Monthly target                                                             */
/* -------------------------------------------------------------------------- */

export function TargetPanel({ target, money }: { target: TargetProgress; money: MoneyFormatter }) {
  const { t } = useTranslation();

  return (
    <Panel title={t('dashboard.target.title')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="tabular text-2xl font-semibold text-content">
          {money(target.achievedMd)}
        </span>
        <span className="text-sm text-content-secondary">
          {t('dashboard.target.of')} <span className="tabular">{money(target.targetMd)}</span>
        </span>
      </div>

      {/* No target configured is a settings gap, not a failure: show what was
          taken and say the bar has nothing to measure against (§6.1). */}
      {target.hasTarget ? (
        <>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-input">
            <div
              className={`h-full rounded-full ${target.isMet ? 'bg-available' : 'bg-primary'}`}
              style={{ inlineSize: `${target.percent}%` }}
              aria-hidden
            />
          </div>
          <p className="mt-2 text-xs text-content-secondary">
            <span className="tabular">{target.percent}%</span>
            {' · '}
            {target.isMet
              ? t('dashboard.target.met')
              : t('dashboard.target.remaining', { amount: money(target.remainingMd) })}
          </p>
        </>
      ) : (
        <p className="mt-3 text-xs text-content-muted">{t('dashboard.target.notConfigured')}</p>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Live occupancy                                                             */
/* -------------------------------------------------------------------------- */

export function OccupancyPanel({ occupancy }: { occupancy: OccupancySnapshot }) {
  const { t } = useTranslation();
  const occupied = occupancy.busy + occupancy.aboutToEnd;

  return (
    <Panel title={t('dashboard.occupancy.title')}>
      {occupancy.total === 0 ? (
        <EmptyNote>{t('dashboard.occupancy.noDevices')}</EmptyNote>
      ) : (
        <>
          <p className="text-sm text-content-secondary">
            {t('dashboard.occupancy.summary', {
              busy: occupied,
              total: occupancy.sellable,
            })}
          </p>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-input">
            <div
              className="h-full rounded-full bg-busy"
              style={{ inlineSize: `${occupancy.percent}%` }}
              aria-hidden
            />
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <CountRow label={t('dashboard.occupancy.busy')} value={occupancy.busy} />
            <CountRow label={t('dashboard.occupancy.aboutToEnd')} value={occupancy.aboutToEnd} />
            <CountRow label={t('dashboard.occupancy.available')} value={occupancy.available} />
            {/* Out-of-service devices are out of the denominator, so showing the
                count explains why occupancy can rise when a PC is taken off. */}
            <CountRow
              label={t('dashboard.occupancy.outOfService')}
              value={occupancy.outOfService}
            />
          </dl>
        </>
      )}
    </Panel>
  );
}

function CountRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-content-secondary">{label}</dt>
      <dd className="tabular text-content">{value}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Device usage by category                                                   */
/* -------------------------------------------------------------------------- */

export function DeviceUsagePanel({
  rows,
  money,
  actions,
}: {
  rows: readonly DeviceCategoryUsage[];
  money: MoneyFormatter;
  /** The page mounts its DAY/MONTH toggle here (owner request). */
  actions?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Panel title={t('dashboard.usage.title')} actions={actions} className="overflow-hidden">
      {rows.length === 0 ? (
        <EmptyNote>{t('dashboard.usage.empty')}</EmptyNote>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-content-muted">
                <Th>{t('dashboard.usage.category')}</Th>
                {/* On a phone this column is what makes the panel scroll
                    sideways; the count lives on the Devices screen anyway. */}
                <Th align="end" className="max-md:hidden">
                  {t('dashboard.usage.devices')}
                </Th>
                {/* Hours joins the devices count off-phone: utilisation % and
                    revenue are the glance; the wide tracked headers were the
                    last 17px of sideways swipe. */}
                <Th align="end" className="max-md:hidden">
                  {t('dashboard.usage.hours')}
                </Th>
                <Th align="end">{t('dashboard.usage.utilisation')}</Th>
                <Th align="end">{t('dashboard.usage.revenue')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.categoryId} className={index % 2 === 0 ? 'bg-row-odd' : 'bg-row-even'}>
                  <Td>{row.categoryName}</Td>
                  <Td align="end" className="tabular text-content-secondary max-md:hidden">
                    {row.deviceCount}
                  </Td>
                  <Td align="end" className="tabular text-content-secondary max-md:hidden">
                    {row.billedHours}
                  </Td>
                  <Td align="end">
                    <div className="flex items-center justify-end gap-2">
                      <span className="tabular">{row.utilisationPercent}%</span>
                      {/* The bar is decoration for the % — on a phone its 56px
                          was exactly what made the panel swipe sideways. */}
                      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-input max-md:hidden">
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{ inlineSize: `${row.utilisationPercent}%` }}
                          aria-hidden
                        />
                      </span>
                    </div>
                  </Td>
                  <Td align="end" className="tabular font-medium">
                    {money(row.revenueMd)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Top customers                                                              */
/* -------------------------------------------------------------------------- */

export function TopCustomersPanel({
  customers,
  money,
}: {
  customers: readonly TopCustomer[];
  money: MoneyFormatter;
}) {
  const { t } = useTranslation();

  return (
    <Panel title={t('dashboard.customers.title')}>
      {customers.length === 0 ? (
        <EmptyNote>{t('dashboard.customers.empty')}</EmptyNote>
      ) : (
        <ul className="space-y-2.5">
          {customers.map((customer) => (
            <li key={customer.customerAccountId}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-content">{customer.customerName}</span>
                <span className="tabular shrink-0 text-content">{money(customer.spendMd)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-input">
                  <span
                    className="block h-full rounded-full bg-primary"
                    style={{ inlineSize: `${customer.sharePercent}%` }}
                    aria-hidden
                  />
                </span>
                {/* `value`, not `count`: `count` would engage i18next's plural
                    machinery, and Arabic's six plural categories are not worth
                    carrying for a bill tally. */}
                <span className="shrink-0 text-xs text-content-muted">
                  {t('dashboard.customers.billCount', { value: customer.billCount })}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Low stock                                                                  */
/* -------------------------------------------------------------------------- */

export function LowStockPanel({ items }: { items: readonly LowStockItem[] }) {
  const { t } = useTranslation();

  return (
    <Panel title={t('dashboard.lowStock.title')}>
      {items.length === 0 ? (
        // An empty list here is good news, so it is phrased as good news.
        <EmptyNote>{t('dashboard.lowStock.allHealthy')}</EmptyNote>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.productId} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                {item.isOutOfStock ? (
                  <TriangleAlert className="h-4 w-4 shrink-0 text-danger" aria-hidden />
                ) : null}
                <span className="truncate text-content">{item.productName}</span>
              </span>
              <span
                className={`tabular shrink-0 ${item.isOutOfStock ? 'text-danger' : 'text-content-secondary'}`}
              >
                {item.isOutOfStock
                  ? t('dashboard.lowStock.outOfStock')
                  : t('dashboard.lowStock.remaining', {
                      quantity: item.stockQuantity,
                      threshold: item.threshold,
                    })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Service charge                                                             */
/* -------------------------------------------------------------------------- */

/**
 * "+ 12,500 IQD service charge" under a sales figure (owner, 2026-09-23).
 *
 * A second line rather than a figure folded into Sales: the contract keeps
 * the charge beside the subtotal, never inside it, so the tile, the monthly
 * target and the Overall Report all go on reading the same Sales. Silent when
 * the period carried none — a venue that charges nothing sees no new line.
 */
export function ServiceChargeHint({
  amountMd,
  money,
}: {
  amountMd: MdString;
  money: MoneyFormatter;
}) {
  // Falsy as well as "0": a payload from an API older than the field has none.
  if (!amountMd || amountMd === '0') return null;
  return (
    <span className="block text-content-muted">
      <MoneyPhrase i18nKey="dashboard.kpi.serviceChargeHint" amount={money(amountMd)} />
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Refills                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The Refills tile (owner, 2026-10-14: "when I refund a balance, it does not
 * get subtracted from the Refills").
 *
 * The figure is the server's NET Refills - top-ups less balance refunds, each
 * on its own business day - rendered verbatim. When the period had refunds the
 * hint shows the arithmetic and still names the period ("1,000,000 topped up -
 * 250,000 refunded · Day"), so a net figure is never a mystery and the tile
 * does not lose its label; a period without refunds reads exactly as the tile
 * always did.
 *
 * Green while Refills are positive (money that arrived as deposits) and the
 * deduction tone once refunds outrun top-ups: a period that paid out more than
 * it took in has a negative figure, and green would call that good news.
 */
export function RefillsTile({
  summary,
  periodLabel,
  money,
}: {
  summary: Pick<PeriodSummary, 'refillMd' | 'refillTopUpsMd' | 'refillReturnsMd'>;
  /** "Day" or "Month": the whole hint when there are no refunds, its tail otherwise. */
  periodLabel: string;
  money: MoneyFormatter;
}) {
  const { t } = useTranslation();
  // Falsy top-ups as well as zero refunds: a payload from an API older than the
  // field cannot state the arithmetic, and "undefined topped up" helps nobody.
  const hasRefunds = !!summary.refillTopUpsMd && BigInt(summary.refillReturnsMd) > 0n;

  return (
    <StatTile
      label={t('dashboard.kpi.refills')}
      value={money(summary.refillMd)}
      tone={BigInt(summary.refillMd) < 0n ? 'danger' : 'positive'}
      hint={
        hasRefunds ? (
          // Each figure alone is `.tabular` (see `MoneyPhrase`): in Arabic and
          // Kurdish that class forces left-to-right, so it may not wrap the
          // words between the two amounts.
          <Trans
            i18nKey="dashboard.kpi.refillsNetHint"
            values={{
              topUps: money(summary.refillTopUpsMd),
              refunds: money(summary.refillReturnsMd),
              period: periodLabel,
            }}
            components={{ money: <span className="tabular" /> }}
          />
        ) : (
          periodLabel
        )
      }
    />
  );
}

/**
 * Must the Estimated sales card say it leaves the service charge out?
 *
 * Whenever the venue charges one — its configured rate, not only a charged bill
 * already landed. Waiting for the first charged checkout hid the note on the
 * first morning of every month, exactly while the sittings on the floor were
 * being estimated without theirs. A bill that carried one today keeps the note
 * on even after the rate is set back to zero: the settled half left it out too.
 * The MONTH's charge does not count — the card is today only.
 */
export function estimateLeavesOutServiceCharge(
  serviceChargePercent: number | undefined,
  dayServiceChargeMd: MdString | undefined,
): boolean {
  return (serviceChargePercent ?? 0) > 0 || (!!dayServiceChargeMd && dayServiceChargeMd !== '0');
}

/**
 * A translated phrase with one money figure in it, where ONLY the figure is
 * `.tabular`.
 *
 * In Arabic and Kurdish `.tabular` forces left-to-right (theme.css), so a span
 * wrapping the whole phrase laid "+ 5,000 IQD من الحسابات" out LTR and an RTL
 * reader met the words before the amount. The strings mark the figure as
 * `<money>{{amount}}</money>`, which keeps word order in the translator's hands.
 */
function MoneyPhrase({ i18nKey, amount }: { i18nKey: string; amount: string }) {
  return (
    <Trans
      i18nKey={i18nKey}
      values={{ amount }}
      components={{ money: <span className="tabular" /> }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Cash boxed today                                                           */
/* -------------------------------------------------------------------------- */

/**
 * "Cash boxed today" (owner, 2026-09-23: "the amount of cash that has been
 * settled to every box of my employees so that i know how much cash is inside
 * my cash drawer right now").
 *
 * The total CASH leads, because that is the question — what should be in the
 * drawer. The account-balance part rides in muted text beside each box: it is
 * business the employee handled, but no notes changed hands, and letting it
 * swell the headline would send the owner looking for money that was never
 * there. Boxes holding something come first (the server orders them, most cash
 * first); empty boxes fold away behind one button so a venue with twelve staff
 * and two on shift does not scroll past ten zeroes on a phone.
 *
 * `null` renders nothing: the server sends null to a viewer who may not see
 * other employees' boxes, and a section that said "0" to them would be a claim
 * about money they were never shown.
 */
export function CashInBoxesPanel({
  summary,
  money,
}: {
  summary: CashInBoxesSummary | null;
  money: MoneyFormatter;
}) {
  const { t } = useTranslation();
  const [emptyOpen, setEmptyOpen] = useState(false);

  if (!summary) return null;

  const holding = summary.employees.filter((row) => row.entryCount > 0);
  const empty = summary.employees.filter((row) => row.entryCount === 0);

  return (
    <Panel title={t('dashboard.cashBoxes.title')}>
      <p className="-mt-2 mb-3 text-xs text-content-muted">{t('dashboard.cashBoxes.hint')}</p>

      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-content-muted">
            {t('dashboard.cashBoxes.totalCash')}
          </p>
          <p
            data-testid="cash-boxes-total"
            className="tabular mt-[5px] text-[26px] leading-tight font-semibold text-primary [text-shadow:var(--glow-primary)]"
          >
            {money(summary.totalCashMd)}
          </p>
        </div>
        {summary.totalAccountMd !== '0' || isNonZeroMd(summary.totalElectronicMd) ? (
          <div className="text-xs text-content-muted">
            {summary.totalAccountMd !== '0' ? (
              <p>
                <MoneyPhrase
                  i18nKey="dashboard.cashBoxes.accountPart"
                  amount={money(summary.totalAccountMd)}
                />
              </p>
            ) : null}
            {/* FIB, Qi, FastPay (2026-09-24): business handled, never notes —
                beside the headline, like the account part, never in it. */}
            {isNonZeroMd(summary.totalElectronicMd) ? (
              <p data-electronic-part>
                <MoneyPhrase
                  i18nKey="dashboard.cashBoxes.electronicPart"
                  amount={money(summary.totalElectronicMd)}
                />
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {holding.length === 0 ? (
        <EmptyNote>{t('dashboard.cashBoxes.empty')}</EmptyNote>
      ) : (
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {holding.map((row) => (
            <CashBoxRow key={row.employeeId} row={row} money={money} />
          ))}
        </ul>
      )}

      {empty.length > 0 ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setEmptyOpen((open) => !open)}
            aria-expanded={emptyOpen}
            className="inline-flex items-center gap-1 rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary transition-colors hover:bg-hover"
          >
            {/* `value`, not `count` — see TopCustomersPanel on Arabic plurals. */}
            {t(emptyOpen ? 'dashboard.cashBoxes.hideEmpty' : 'dashboard.cashBoxes.showEmpty', {
              value: empty.length,
            })}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${emptyOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
          {emptyOpen ? (
            <ul className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {empty.map((row) => (
                <CashBoxRow key={row.employeeId} row={row} money={money} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

function CashBoxRow({ row, money }: { row: CashInBoxEmployeeRow; money: MoneyFormatter }) {
  const { t, i18n } = useTranslation();

  const lastReset = row.lastResetAt
    ? row.lastResetByName
      ? t('dashboard.cashBoxes.lastReset', {
          name: row.lastResetByName,
          time: formatResetInstant(row.lastResetAt, i18n.language),
        })
      : // A reset recorded before the row kept who pressed the button: the
        // time is known, the person is not, and it is not guessed.
        t('dashboard.cashBoxes.lastResetUnknown', {
          time: formatResetInstant(row.lastResetAt, i18n.language),
        })
    : t('dashboard.cashBoxes.neverReset');

  return (
    <li className="min-w-0 rounded-control border border-subtle px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium text-content">{row.name}</span>
        {/* A box can hold NEGATIVE cash after refunds: the drawer owes money. */}
        <span
          className={`tabular shrink-0 text-sm font-semibold ${
            row.cashMd.startsWith('-') ? 'text-danger' : 'text-content'
          }`}
        >
          {money(row.cashMd)}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-baseline justify-between gap-x-3 text-[11px] text-content-muted">
        <span className="min-w-0 truncate">{lastReset}</span>
        {row.accountMd !== '0' ? (
          <span className="shrink-0">
            <MoneyPhrase i18nKey="dashboard.cashBoxes.account" amount={money(row.accountMd)} />
          </span>
        ) : null}
        {isNonZeroMd(row.electronicMd) ? (
          <span className="shrink-0">
            <MoneyPhrase
              i18nKey="dashboard.cashBoxes.electronic"
              amount={money(row.electronicMd)}
            />
          </span>
        ) : null}
      </div>
    </li>
  );
}

/** `undefined` is a server from before the electronic component. */
function isNonZeroMd(md: string | undefined): md is string {
  return md !== undefined && md !== '0';
}

/**
 * A reset instant with its date: a box last emptied three days ago must not
 * read as "20:14" and pass for this evening.
 */
export function formatResetInstant(iso: string, locale: string): string {
  return formatInstantWithDate(iso, locale);
}

/* -------------------------------------------------------------------------- */
/* Cash in drawer                                                             */
/* -------------------------------------------------------------------------- */

/**
 * "Cash in drawer" (2026-10-12) — the owner's 2026-09-23 question, "how much
 * cash is inside my cash drawer right now", answered off the venue's ONE
 * drawer instead of a sum of employee boxes: the open session's float plus
 * every cash movement stamped to it since.
 *
 * `null` renders nothing: the server sends null to a viewer who may not see
 * it, and a section that said "0" to them would be a claim about money they
 * were never shown. A null figure inside a present drawer is the same rule
 * for the number alone.
 *
 * Forgetting to close breaks nothing (owner, 2026-10-12); the warning is the
 * whole consequence, and it says since when.
 */
export function CashDrawerPanel({
  drawer,
  money,
}: {
  drawer: DashboardPayload['cashDrawer'] | null;
  money: MoneyFormatter;
}) {
  const { t, i18n } = useTranslation();

  if (!drawer) return null;

  if (!drawer.enabled) {
    return (
      <Panel title={t('dashboard.cashDrawer.title')}>
        <EmptyNote>{t('dashboard.cashDrawer.notInUse')}</EmptyNote>
      </Panel>
    );
  }

  return (
    <Panel title={t('dashboard.cashDrawer.title')}>
      <p className="-mt-2 mb-3 text-xs text-content-muted">{t('dashboard.cashDrawer.hint')}</p>
      {drawer.expectedCashMd !== null ? (
        <p
          data-testid="cash-drawer-expected"
          className={`tabular text-[26px] leading-tight font-semibold ${
            // The drawer can owe money: payouts past the float.
            drawer.expectedCashMd.startsWith('-')
              ? 'text-danger'
              : 'text-primary [text-shadow:var(--glow-primary)]'
          }`}
        >
          {money(drawer.expectedCashMd)}
        </p>
      ) : null}
      {drawer.openedAt ? (
        <p className="mt-1 text-xs text-content-secondary">
          {t('dashboard.cashDrawer.openSince', {
            time: formatInstantWithDate(drawer.openedAt, i18n.language),
          })}
        </p>
      ) : null}
      {drawer.notCountedSince ? (
        <p
          data-not-counted={drawer.notCountedSince}
          className="mt-2 flex items-center gap-1.5 text-sm font-medium text-ending"
        >
          <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden />
          {t('dashboard.cashDrawer.notCounted', {
            time: formatInstantWithDate(drawer.notCountedSince, i18n.language),
          })}
        </p>
      ) : null}
    </Panel>
  );
}

/**
 * An instant with its date: a drawer last counted three days ago must not
 * read as "20:14" and pass for this evening.
 */
export function formatInstantWithDate(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `HH:mm` in the reader's locale, or an em dash if the instant is unusable. */
export function formatClock(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/* -------------------------------------------------------------------------- */
/* Table cells                                                                */
/* -------------------------------------------------------------------------- */

function Th({
  children,
  align,
  className = '',
}: {
  children: ReactNode;
  align?: 'end';
  className?: string;
}) {
  return (
    <th
      className={`px-3 py-2 font-medium max-md:px-2 ${align === 'end' ? 'text-end' : 'text-start'} ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  className = '',
}: {
  children: ReactNode;
  align?: 'end';
  className?: string;
}) {
  return (
    <td
      className={`px-3 max-md:px-2 py-2 ${align === 'end' ? 'text-end' : 'text-start'} ${className}`}
    >
      {children}
    </td>
  );
}
